<script lang="ts" setup>
import OrderAPI from '~/api/order';
import { COUPON_DATA_STORAGE_KEY } from '~/api/plugins/coupon';
import { OrderValidationStatusEnum } from '~/composables/useOrderValidation';

const { t } = useI18n();
const router = useRouter();
const orderStore = useOrderStore();
const modalStore = useModalStore();
const subscribeProductsStore = useSubscribeProductsStore();
const couponStore = useCouponStore();
const dataLayer = useDataLayer();

const isOnlinePayment = computed(
  () => orderStore.payment.current?.type === 'online',
);

const activateOrder = reactive(useRequest(OrderAPI.activate));
const { execute, isLoading } = usePayment({
  onChecking: async () => {
    const activateOrderData = await activateOrder.execute();
    if (!activateOrderData) throw new Error('Empty payment data');

    couponStore.clearCoupon();

    orderStore.prepareSuccessfulData(
      activateOrderData.id,
      activateOrderData.message,
    );
    return { orderId: activateOrderData.id, enabled: isOnlinePayment.value };
  },
  onClear: () => {
    orderStore.clearCartData();
    subscribeProductsStore.clear();
    localStorage.removeItem(COUPON_DATA_STORAGE_KEY);
  },
  onSuccess: () => {
    dataLayer.append(
      {
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField: {
              id: orderStore.result.orderId,
              revenue: orderStore.summary.basketPrice,
              shipping: orderStore.summary.deliveryPrice,
            },
            products: orderStore.cartData.map((cartItem) => ({
              id: cartItem.productId,
              name: cartItem.name,
              price: cartItem.price,
              quantity: cartItem.quantity,
            })),
          },
        },
      },
      ['user'],
    );

    router.push('/order/successful');
  },
  onCancel: () => {
    modalStore.showInfo({
      title: t('message.cancel'),
      action: t('message.action'),
    });

    router.push('/catalog');
  },
});

const validation = useOrderValidation();
</script>

<template>
  <div>
    <BaseButton
      @click="execute"
      :disabled="validation.status !== OrderValidationStatusEnum.success"
      :loading="isLoading"
      dark
      block
      content-class="orderPaymentButton__content"
      data-test-id="order-payment-button"
    >
      <Transition name="reel">
        <span v-if="isOnlinePayment" class="orderPaymentButton__state">
          {{ t('payment') }}
        </span>
        <span v-else class="orderPaymentButton__state">
          {{ t('order') }}
        </span>
      </Transition>
    </BaseButton>

    <BaseCollapse
      :visible="validation.status === OrderValidationStatusEnum.failed"
      :fixed-duration="200"
      content-class="orderPaymentButton__message"
    >
      <Transition name="reel">
        <span
          v-if="validation.message"
          :key="validation.message"
          class="orderPaymentButton__error"
        >
          {{ t(validation.message) }}
        </span>
      </Transition>
    </BaseCollapse>
  </div>
</template>

<i18n>
ru:
  payment: Перейти к оплате
  order: Заказать
  message:
    cancel: Оформление заказа отменено. Найти его можно в личном кабинете.
    action: Продолжить
  error:
    cart_empty: Для оформления заказа необходимо выбрать товары
    syncing: Подождите синхронизации данных заказа
    user_invalid: Данные получателя заполнены не правильно
    delivery_invalid: Данные для доставки заполнены не правильно
    payment_missing: Необходимо выбрать подходящий способ оплаты
    payment_invalid: Данные для оплаты заполнены не правильно
    order_incomplete: Данные оформления заказа заполнены не полностью

en:
  payment: Proceed to payment
  order: Order
  message:
    cancel: The order has been cancelled. You can find it in your personal account.
    action: Continue
  error:
    cart_empty: You must select products before placing an order
    syncing: Please wait for the order data to sync
    user_invalid: The recipient’s information is not filled in correctly
    delivery_invalid: Delivery information is not filled in correctly
    payment_missing: A suitable payment method must be selected
    payment_invalid: Payment information is not filled in correctly
    order_incomplete: Order data is not fully completed
</i18n>

<style lang="scss">
.orderPaymentButton {
  &__content {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__state {
    display: block;
    position: absolute;
  }

  &__message {
    position: relative;
    padding-top: 8px;
    height: 26px;
  }

  &__error {
    display: block;
    position: absolute;
    inset: 0;
    top: 8px;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.5px;
    color: #eb5757;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
